import React, { useContext, useEffect, useState } from 'react'

import { useTranslation } from '../../i18n'

import { useRouter } from 'next/router'
import { useAuth } from 'v2/hooks/useAuth'
import { UserContext } from 'v2/context/user'
import SLoadingIndicator from 'components/opinionless/SLoadingIndicator'

import { useSTheme } from 'styles/theme'

import SBox from 'components/opinionless/SBox'

import {
    useMeQuery,
    useMyUpcomingPaymentLazyQuery,
    useSurveyAvailableLazyQuery,
    useMyRecentPaymentsQuery,
    PaymentStatus,
} from '../../types/graphql'
import PageHeader from '../../components/PageHeader'
import { useMemo } from 'react'

import moment from 'moment-timezone'
import HomeUpcomingPayment from './UpcomingPayment'

import ConditionalRender from 'utils/conditionalRender'
import XAuthPage from 'v2/containers/xAuthPage'
import styles from './style.module.scss'
import HomePaymentMethodV2 from './PaymentMethodV2'
import XBanner from 'v2/components/XBanner'
import ToggleablePaymentsTable from './ToggleablePaymentsTable'

const HomeContainer: React.FC = () => {
    const { t } = useTranslation(window.LANG_NS)
    const { data, loading } = useMeQuery()
    const router = useRouter()

    const { loginLoading } = useAuth()
    const { authenticatedUser } = useContext(UserContext)

    const theme = useSTheme()
    const [surveyURL, setSurveyURL] = useState('#')
    const [surveyAvailableData, setSurveyAvailableData] = useState(null)

    const typeformId = window.ORGANIZATION_CONFIGS?.typeformId
    const typeformSubdomain = window.ORGANIZATION_CONFIGS?.typeformSubdomain
    const paymentProfileComplete =
        (authenticatedUser && authenticatedUser.paymentProfile?.transferMethods.edges.length) ||
        (authenticatedUser && authenticatedUser.isSuperuser > 0)

    const surveyButtonDisabled =
        (!window.ORGANIZATION_CONFIGS.isTypeformBound || !surveyAvailableData?.surveyAvailable) &&
        !window.location.href.includes('longbeach')

    const [getSurveyAvailable, { data: sAData }] = useSurveyAvailableLazyQuery({
        pollInterval: 3000,
        fetchPolicy: 'no-cache',
    })

    const { data: recentPaymentsData } = useMyRecentPaymentsQuery()

    useEffect(() => {
        if (authenticatedUser && window.ORGANIZATION_CONFIGS.isTypeformBound) {
            if (sAData) {
                setSurveyAvailableData(sAData)
            } else {
                getSurveyAvailable()
            }
        }
    }, [authenticatedUser, sAData])

    useEffect(() => {
        if (localStorage.getItem('isUserFromSignUp')) {
            const handler = setTimeout(() => {
                localStorage.removeItem('isUserFromSignUp')
            }, 5000)

            return () => {
                clearTimeout(handler)
            }
        }
    }, [])

    useEffect(() => {
        if (window.ORGANIZATION_CONFIGS.isTypeformBound && authenticatedUser) {
            const email = encodeURIComponent(authenticatedUser.email)
            setSurveyURL(
                `https://${typeformSubdomain}.typeform.com/to/${typeformId}/#email=${email}&user_id=${encodeURIComponent(
                    authenticatedUser.id
                )}`
            )
        }
    }, [authenticatedUser])

    useEffect(() => {
        if (!loginLoading && authenticatedUser) {
            if (!paymentProfileComplete) {
                router.replace(
                    `/v2${
                        window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment
                    }/sign-up`
                )
            }
        }
    }, [loginLoading, paymentProfileComplete])

    const homeHeaderText = useMemo(() => {
        if (!data) {
            return null
        } else {
            const currentHour = moment().hour()

            if (currentHour >= 4 && currentHour < 12) {
                return t('home_header_morning', { name: data.me.person?.profile?.firstName })
            } else if (currentHour >= 12 && currentHour < 18) {
                return t('home_header_afternoon', { name: data.me.person?.profile?.firstName })
            } else {
                return t('home_header_evening', { name: data.me.person?.profile?.firstName })
            }
        }
    }, [data, t])

    const [getUpcomingPayment, { data: upcomingData }] = useMyUpcomingPaymentLazyQuery()

    useEffect(() => {
        if (authenticatedUser) {
            if (!upcomingData) {
                void getUpcomingPayment()
            }
        }
    }, [authenticatedUser, upcomingData])

    const onSurveyClick = () => {
        if (!surveyButtonDisabled) {
            window.open(surveyURL, '_blank')
        }
    }

    const onContactUsClick = () => {
        router.push(`/v2/${router.query['program-name']}/help`)
    }

    return (
        <XAuthPage sidebar={true}>
            <div className={styles.wrapper}>
                {loading || !paymentProfileComplete ? (
                    <SLoadingIndicator width="100%" height={'100%'} color={theme.colors.secondaryColor} />
                ) : (
                    <>
                        <SBox flex="1 1 auto">
                            <PageHeader>{homeHeaderText}</PageHeader>
                            <ConditionalRender
                                if={recentPaymentsData?.myRecentPayments[0]?.status === PaymentStatus.Failed}
                            >
                                <div className={styles.payment__banner}>
                                    <XBanner
                                        title={t('transfer_failed_banner_title')}
                                        description={t('contact_us_banner_description')}
                                        buttonTitle={t('contact_us_button')}
                                        buttonAction={onContactUsClick}
                                        type="warning"
                                    ></XBanner>
                                </div>
                            </ConditionalRender>
                            <ConditionalRender
                                if={
                                    window &&
                                    window.ORGANIZATION_CONFIGS.isTypeformBound &&
                                    surveyAvailableData?.surveyAvailable
                                }
                            >
                                <div className={styles.payment__banner}>
                                    <XBanner
                                        title={t('survey_incomplete_banner_title')}
                                        description={t('take_survey_banner_description')}
                                        buttonTitle={t('take_survey_button')}
                                        buttonAction={onSurveyClick}
                                        isButtonDisabled={surveyButtonDisabled}
                                        type="warning"
                                    ></XBanner>
                                </div>
                            </ConditionalRender>

                            <div className={styles.payment__cards}>
                                <HomePaymentMethodV2 mb={[6, 9, 9]} mr={[0, 9, 9]} />
                                <HomeUpcomingPayment mb={[6, 9, 9]} />
                            </div>
                            <ToggleablePaymentsTable />
                        </SBox>
                    </>
                )}
            </div>
        </XAuthPage>
    )
}

export default HomeContainer
